import { ItemType } from "../../../clients/graphqlTypes.js";
import { getDefaultCreatableSubItemTypes } from "./constraints.js";

// ---------------------------------------------------------------------------
// Generic tree item interfaces
// ---------------------------------------------------------------------------

export interface TreeItemBase {
  id: string;
  parentId?: string | null;
}

export interface MoveTargetItem extends TreeItemBase {
  type: ItemType;
  linkedQuoteTemplate?: { id: string } | null;
}

// ---------------------------------------------------------------------------
// canHaveCreateInput
// ---------------------------------------------------------------------------

export const canHaveCreateInput = (
  itemType: ItemType,
  documentItemTypes: ItemType[],
  itemHasLinkedTemplate: boolean
): boolean => {
  if (itemHasLinkedTemplate) return false;
  return (
    getDefaultCreatableSubItemTypes(itemType, documentItemTypes).length > 0
  );
};

// ---------------------------------------------------------------------------
// isDescendantOf — checks if candidateId is a descendant of ancestorId
// ---------------------------------------------------------------------------

export function isDescendantOf<T extends TreeItemBase>(
  candidateId: string,
  ancestorId: string,
  items: T[]
): boolean {
  const itemsById = new Map(items.map(i => [i.id, i]));
  let current = itemsById.get(candidateId);
  while (current?.parentId) {
    if (current.parentId === ancestorId) return true;
    current = itemsById.get(current.parentId);
  }
  return false;
}

// ---------------------------------------------------------------------------
// getValidMoveTargets — filters items to valid parent candidates for a set of items
// ---------------------------------------------------------------------------

export function getValidMoveTargets<T extends MoveTargetItem>(
  items: T[],
  itemIds: string[],
  documentItemTypes: ItemType[]
): T[] {
  const itemSet = new Set(itemIds);
  const selectedItems = items.filter(i => itemSet.has(i.id));
  const selectedTypes = selectedItems
    .map(i => i.type)
    .filter(Boolean) as ItemType[];

  // If all items share the same parent, exclude that parent
  const parentIds = new Set(selectedItems.map(i => i.parentId).filter(Boolean));
  const commonParentId = parentIds.size === 1 ? [...parentIds][0] : null;

  return items.filter(candidate => {
    if (itemSet.has(candidate.id)) return false;
    if (candidate.linkedQuoteTemplate) return false;
    if (commonParentId && candidate.id === commonParentId) return false;

    const creatableTypes = getDefaultCreatableSubItemTypes(
      candidate.type,
      documentItemTypes
    );
    if (!selectedTypes.every(ct => creatableTypes.includes(ct))) return false;

    for (const id of itemIds) {
      if (isDescendantOf(candidate.id, id, items)) return false;
    }

    return true;
  });
}

// ---------------------------------------------------------------------------
// sortItemIdsByTreeOrder — sorts item IDs by their path in the tree
// ---------------------------------------------------------------------------

export function sortItemIdsByTreeOrder(
  itemIds: string[],
  items: { id: string; pathSortable: string }[]
): string[] {
  const itemsById = new Map(items.map(i => [i.id, i]));
  return [...itemIds].sort((a, b) => {
    const itemA = itemsById.get(a);
    const itemB = itemsById.get(b);
    if (!itemA || !itemB) return 0;
    return itemA.pathSortable.localeCompare(itemB.pathSortable);
  });
}
